import React, { type FC, type ReactNode, useCallback } from 'react';

type Props = {
    children: ReactNode;
};

/**
 * A wrapper component that prevents tooltips from appearing when hovering over List.Item
 * or other design-system components with truncated text. It uses an invisible overlay to capture mouse
 * hover events while forwarding click events to the underlying element.
 *
 * Usage:
 * <NoTooltipWrapper>
 *     <List.Item ...>
 *         <TruncatedText>{longText}</TruncatedText>
 *     </List.Item>
 * </NoTooltipWrapper>
 */
const NoTooltipWrapper: FC<Props> = ({ children }) => {
    const handleOverlayClick = useCallback((e: React.MouseEvent) => {
        const overlay = e.currentTarget as HTMLElement;

        overlay.style.pointerEvents = 'none';

        const elementBelow = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement;

        overlay.style.pointerEvents = 'auto';

        if (elementBelow) {
            elementBelow.click();
        }
    }, []);

    return (
        <div style={{ position: 'relative' }}>
            {children}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
            <div
                onClick={handleOverlayClick}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1,
                    cursor: 'pointer',
                }}
            />
        </div>
    );
};

export default NoTooltipWrapper;
